import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Alert, Typography, Row, Col, Button, Checkbox, Result } from "antd";
import { useQuery } from "@apollo/client";
import { GET_INVENTORY_BY_PLACE } from "../graphql/queries";
import PlaceOverview from "../components/PlaceOverview";
import AnimatedLogo from "../components/AnimatedLogo";
import IconComponent from "../components/IconComponent";
import { Inventory, PlaceWithDistance } from "../types";
import { useAppTheme } from "../theme/ThemeProvider";
import { theme } from "antd";

const { Title } = Typography;

interface PlaceDetailPageProps {
  userSelectedRoles?: string[];
  inventory: string[];
  setInventory: (inventory: string[]) => void;
  setPlaceDetail: (place: PlaceWithDistance | { id: string; name: string } | null) => void;
  setIsOneInventory: (isOne: boolean) => void;
  location?: { latitude: number; longitude: number } | null;
  placesForMap?: PlaceWithDistance[];
}

export default function PlaceDetailPage({
  inventory,
  setInventory,
  setPlaceDetail,
  setIsOneInventory,
  location,
  placesForMap,
}: PlaceDetailPageProps) {
  const { themeName } = useAppTheme();
  const { token } = theme.useToken();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [placeNewLocation, setPlaceNewLocation] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  const userId = localStorage.getItem("userId") || "";
  const eventId = localStorage.getItem("eventId") || "";

  const { data, loading, refetch } = useQuery<{ getInventoryByPlace: { inventory: Inventory[]; success: boolean } }>(
    GET_INVENTORY_BY_PLACE,
    {
      variables: { input: { userId: userId || null, eventId, placeId: id || "" } },
      skip: !id || !eventId,
    }
  );

  const items = useMemo(() => {
    const rawItems = data?.getInventoryByPlace?.inventory || [];
    const uniqueById = new Map<string, Inventory>();
    for (const item of rawItems) {
      if (!item?.id) continue;
      if (!uniqueById.has(item.id)) {
        uniqueById.set(item.id, item);
      }
    }
    return Array.from(uniqueById.values());
  }, [data?.getInventoryByPlace?.inventory]);
  const place = useMemo(() => 
    items.length > 0 ? { name: items[0].place_name || "", id: items[0].place_id } : null,
    [items]
  );

  const toggleSelect = (itemId: string) => {
    setSelected((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const currentPlace = useMemo(() => placesForMap?.find((p) => p.id === id), [placesForMap, id]);
  const hasValidGeo =
    currentPlace?.geo_location != null &&
    currentPlace.geo_location.latitude != null &&
    currentPlace.geo_location.longitude != null;

  useEffect(() => {
    // Po refreshi stránky /place/:id často chybí placesForMap v paměti aplikace.
    // Bez něj neumíme spolehlivě určit geo polohu místa, proto vrátíme uživatele na hlavní stránku.
    if (!loading && id && items.length > 0 && !currentPlace) {
      setPlaceDetail(null);
      navigate("/");
    }
  }, [loading, id, items.length, currentPlace, setPlaceDetail, navigate]);

  useEffect(() => {
    // Pokud má místo geolokaci a je jen jedna položka inventáře, rovnou přejdeme na otázky
    if (items?.length === 1 && hasValidGeo) {
      setInventory([items[0].id]);
      setIsOneInventory(true);
      if (place) setPlaceDetail(currentPlace ?? place);
      navigate(`/questions`);
    }
  }, [items, place, currentPlace, hasValidGeo, setInventory, setIsOneInventory, setPlaceDetail, navigate]);

  const handleContinue = () => {
    setInventory(selected);
    if (place) setPlaceDetail(currentPlace ?? place);
    setIsOneInventory(false);
    navigate(`/questions`);
  };

  const fetchDetail = async (setLocation?: string) => {
    await refetch();
    if (setLocation) {
      setPlaceNewLocation(true);
    }
  };

  // Když BE vrátí místo bez geolokace (latitude/longitude null), vyžadujeme,
  // aby uživatel označil polohu stánku (jako ve starém frontendu).
  const needsLocation = items.length > 0 && !placeNewLocation && !hasValidGeo;

  if (needsLocation) {
    return (
      <PlaceOverview
        placeId={id || items[0]?.place_id || ""}
        placeName={currentPlace?.name ?? place?.name ?? undefined}
        fetchPlacesAgain={() => fetchDetail("setLocation")}
        buttonType="full"
        location={undefined}
      />
    );
  }

  return (
    <div style={{ padding: 16 }}>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <AnimatedLogo loading="spinner" />
        </div>
      ) : items.length === 0 ? (
        <Result title="Toto místo bohužel neobsahuje žádná kritéria k hodnocení." />
      ) : (
        <>
          {!userId && (
            <Title level={4} >
          Vyberte prosím jednu nebo více položek k hodnocení:
            </Title>
          )}
         { userId && <Title level={3}>{place?.name}</Title>}
          <Row gutter={[16, 16]}>
            {items.map((item) => (
              <Col key={item.id} span={24}>
                <Card
                  hoverable
                  onClick={() => toggleSelect(item.id)}
                  style={{
                    border: selected.includes(item.id)
                      ? "2px solid #16d4e0"
                      : themeName === "dark"
                        ? "1px solid rgba(255,255,255,0.06)"
                        : undefined,
                    background:
                      themeName === "dark"
                        ? "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015))"
                        : "#ffffff80",
                    padding: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 16px",
                    }}
                  >
                    <div style={{ fontSize: 28, marginRight: 16 }}>
                      <IconComponent icon={item.icon} size={28} />
                    </div>
                    <div style={{ flexGrow: 1, fontSize: 18 }}>{item.name}</div>
                    <Checkbox
                      checked={selected.includes(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {selected.length > 0 && (
            <Button
              type="primary"
              block
              onClick={handleContinue}
              style={{
                marginTop: 24,
                height: 64,
                fontSize: 20,
                fontWeight: "bold",
              }}
            >
              Pokračovat
            </Button>
          )}
        </>
      )}
    </div>
  );
}
